/**
 * Quem codifica o video que sai desta maquina: a placa de video ou o
 * processador.
 *
 * Navegador nao deixa escolher isso direto — nao existe "use a GPU" no
 * WebRTC. O que existe e escolher o CODEC, e no Chrome a escolha do codec ja
 * decide o encoder:
 *
 * - **H.264** vai pro encoder de hardware da placa (D3D12 / Media Foundation
 *   no Windows, VideoToolbox no Mac) — mas so nas variantes que a placa
 *   aceita, e por isso a variante e escolhida com cuidado logo abaixo. Sem
 *   encoder de hardware, o proprio Chrome cai pro OpenH264, que ainda pesa
 *   menos que o VP8.
 * - **VP8** e sempre software (libvpx). E o que o navegador escolhe sozinho
 *   quando ninguem pede nada — e em tela 1080p a 60 fps ele sozinho ocupa um
 *   nucleo inteiro. Na malha P2P sao um encoder por espectador: e o que deixa
 *   a maquina de quem transmite inteira travada.
 *
 * As duas opcoes existem porque nenhuma serve pra todo mundo: em maquina com
 * processador fraco a GPU e o que salva, e em placa com driver ruim o H.264
 * por hardware e que sai com defeito (blocos verdes, imagem congelando).
 */
export type ShareEncoder = 'gpu' | 'cpu'

export interface EncoderPreset {
    label: string
    description: string
    /** Codec que leva a esse encoder, no formato do `mimeType` do WebRTC. */
    mimeType: string
}

export const ENCODER_PRESETS: Record<ShareEncoder, EncoderPreset> = {
    cpu: {
        label: 'CPU',
        description: 'Processador (VP8) — pesa mais na maquina',
        mimeType: 'video/VP8'
    },
    gpu: {
        label: 'GPU',
        description: 'Placa de video (H.264) — alivia o processador',
        mimeType: 'video/H264'
    }
}

/**
 * O que a pessoa escolheu no menu. `auto` e o padrao e segue a deteccao da
 * placa; `gpu` e `cpu` fixam o encoder nesta maquina.
 */
export type EncoderChoice = 'auto' | ShareEncoder

export const ENCODER_CHOICES: Record<EncoderChoice, { label: string; description: string }> = {
    auto: {
        label: 'Automatico',
        description: 'GPU quando a placa codifica; senao, CPU — o padrao'
    },
    gpu: ENCODER_PRESETS.gpu,
    cpu: ENCODER_PRESETS.cpu
}

export const ENCODER_ORDER: EncoderChoice[] = ['auto', 'gpu', 'cpu']

/**
 * O que o automatico usa enquanto nao se sabe se a placa codifica: CPU, o VP8
 * que o navegador escolhe sozinho e que funciona em qualquer maquina.
 * `detectHardwareEncoders` responde em milissegundos, bem antes de alguem
 * conseguir escolher uma tela.
 */
export const DEFAULT_ENCODER: ShareEncoder = 'cpu'

/**
 * Lembrado por maquina, e nao por sala: quem tem a placa e o processador e o
 * computador.
 *
 * Chave nova de proposito. A antiga (`screen-share:encoder`) guardava `cpu`
 * de quem so tinha clicado no menu — inclusive na epoca em que CPU era o
 * padrao e a GPU so uma opcao — e escolha guardada vencia a deteccao pra
 * sempre: PC com placa seguia no processador sem ninguem saber por que.
 */
const CHOICE_KEY = 'screen-share:encoder-choice'

export function loadEncoderChoice(): EncoderChoice {
    try {
        const saved = localStorage.getItem(CHOICE_KEY)
        return saved === 'gpu' || saved === 'cpu' ? saved : 'auto'
    } catch {
        return 'auto'
    }
}

export function saveEncoderChoice(choice: EncoderChoice): void {
    try {
        if (choice === 'auto') localStorage.removeItem(CHOICE_KEY)
        else localStorage.setItem(CHOICE_KEY, choice)
    } catch {
        // Modo privado / storage bloqueado: vale so ate fechar a aba.
    }
}

/**
 * O encoder de fato: a escolha fixa, ou, no automatico, GPU quando a placa
 * codifica H.264 e CPU quando nao (ou quando nao da pra saber).
 */
export function resolveEncoder(choice: EncoderChoice, gpuAvailable: boolean | null): ShareEncoder {
    if (choice !== 'auto') return choice
    return gpuAvailable ? 'gpu' : DEFAULT_ENCODER
}

/**
 * Perfil e modo de empacotamento de uma variante de H.264 (`4200/1`), ou
 * `null` pra qualquer outro codec.
 *
 * O nivel fica de fora de proposito: e o que menos importa pro encoder e o que
 * mais varia entre a capacidade do navegador e o que a negociacao devolve.
 * Sem os campos no fmtp, valem os padroes da RFC 6184 (`420010`, modo 0).
 */
function h264Variant(codec: RTCRtpCodec): string | null {
    if (codec.mimeType.toLowerCase() !== 'video/h264') return null
    const fmtp = (codec.sdpFmtpLine ?? '').toLowerCase()
    const profile = /profile-level-id=([0-9a-f]{4})/.exec(fmtp)?.[1] ?? '4200'
    const mode = /packetization-mode=(\d)/.exec(fmtp)?.[1] ?? '0'
    return `${profile}/${mode}`
}

/**
 * Variantes de H.264 que ESTE navegador codifica na placa de video.
 *
 * `null` enquanto `detectHardwareEncoders` nao respondeu (ou o navegador nao
 * sabe dizer). Aprendido, e nao chutado: no Chrome 154 com placa NVIDIA, a
 * constrained baseline (`42e01f`) — o perfil "seguro" de todo tutorial — cai
 * no OpenH264, e so a baseline (`42001f`), a main e a high vao pra placa.
 */
let hardwareVariants: Set<string> | null = null

/**
 * Pergunta ao navegador quais variantes de H.264 ele codifica em hardware.
 *
 * `mediaCapabilities` com `type: 'webrtc'` responde `powerEfficient` por
 * variante, e a resposta bate com o encoder que o WebRTC de fato abre. Devolve
 * se ha alguma (`null` = sem como saber). Uma vez por aba basta: a placa nao
 * muda com a pagina aberta.
 */
export async function detectHardwareEncoders(): Promise<boolean | null> {
    const codecs = globalThis.RTCRtpSender?.getCapabilities?.('video')?.codecs ?? []
    const capabilities = typeof navigator === 'undefined' ? undefined : navigator.mediaCapabilities
    if (typeof capabilities?.encodingInfo !== 'function') return null

    const found = new Set<string>()
    let answered = 0
    await Promise.all(codecs.map(async (codec) => {
        const variant = h264Variant(codec)
        if (!variant) return
        try {
            const info = await capabilities.encodingInfo({
                type: 'webrtc',
                video: {
                    contentType: codec.sdpFmtpLine ? `${codec.mimeType};${codec.sdpFmtpLine}` : codec.mimeType,
                    width: 1920,
                    height: 1080,
                    bitrate: 6_000_000,
                    framerate: 30
                }
            })
            answered += 1
            if (info.supported && info.powerEfficient) found.add(variant)
        } catch {
            // Variante que o navegador nao sabe avaliar: fica de fora.
        }
    }))
    if (answered === 0) return null
    hardwareVariants = found
    return found.size > 0
}

/**
 * O quanto um codec serve pro encoder escolhido — menor e melhor.
 *
 * Pra GPU, as variantes de H.264 que o navegador disse codificar em hardware
 * vem primeiro. Antes da resposta (ou sem ela), a ordem que o hardware aceita
 * no Chrome: baseline e main com `packetization-mode=1`. O modo 0 por ultimo
 * — ele nao fatia quadro grande e nenhum encoder de placa o usa no Chrome.
 */
function rank(codec: RTCRtpCodec, encoder: ShareEncoder): number {
    if (codec.mimeType.toLowerCase() !== ENCODER_PRESETS[encoder].mimeType.toLowerCase()) return 9
    const variant = h264Variant(codec)
    if (encoder !== 'gpu' || !variant) return 0
    if (hardwareVariants?.has(variant)) return 0
    const [profile, mode] = variant.split('/')
    if (mode !== '1') return 3
    return profile === '4200' || profile === '4d00' ? 1 : 2
}

/**
 * A lista inteira, com o codec do encoder escolhido na frente.
 *
 * Reordena, nunca filtra: se o outro lado nao decodifica o preferido, a
 * negociacao cai no proximo da lista em vez de nao ter video nenhum. `sort` e
 * estavel, entao o resto segue na ordem do navegador.
 */
export function orderCodecs<T extends RTCRtpCodec>(codecs: T[], encoder: ShareEncoder): T[] {
    return [...codecs].sort((a, b) => rank(a, encoder) - rank(b, encoder))
}

/**
 * Poe o codec escolhido no topo da proxima negociacao deste transceiver.
 *
 * Vale pra offer e pra answer que sairem daqui. Best-effort: navegador sem
 * `setCodecPreferences` (ou que recusa a lista) segue com a ordem dele, e a
 * transmissao continua igual — so sem a preferencia.
 */
export function preferCodec(transceiver: RTCRtpTransceiver, encoder: ShareEncoder): void {
    if (typeof transceiver.setCodecPreferences !== 'function') return
    // O spec mudou de lado no meio do caminho: a lista ja foi validada contra
    // as capacidades de envio e hoje e contra as de recepcao. Tenta as duas.
    for (const source of [globalThis.RTCRtpReceiver, globalThis.RTCRtpSender]) {
        const codecs = source?.getCapabilities?.('video')?.codecs
        if (!codecs?.length) continue
        try {
            transceiver.setCodecPreferences(orderCodecs(codecs, encoder))
            return
        } catch {
            // Tenta a proxima fonte.
        }
    }
}

/** `codec` por camada: no Chrome desde a 119, ainda fora da lib do TypeScript. */
type EncodingWithCodec = RTCRtpEncodingParameters & { codec?: RTCRtpCodec }

/**
 * Troca o codec de envio dentro dos parametros, sem renegociar.
 *
 * E o que faz a escolha valer na hora, com a transmissao no ar, e o que a
 * protege quando duas pessoas transmitem pela mesma linha do SDP — ali a ordem
 * que vale e a de quem respondeu a negociacao, nao a de quem envia.
 *
 * So escolhe entre os codecs JA negociados (`params.codecs`): pedir um que o
 * outro lado nao aceitou faz o `setParameters` inteiro falhar, e com ele iria
 * junto o teto de bitrate. Sem o preferido na lista, sai da frente e deixa o
 * que a negociacao decidiu. Navegador que nao conhece `codec` ignora o campo.
 */
export function applySendCodec(params: RTCRtpSendParameters, encoder: ShareEncoder): void {
    const wanted = ENCODER_PRESETS[encoder].mimeType.toLowerCase()
    const match = orderCodecs(params.codecs ?? [], encoder)
        .find((codec) => codec.mimeType.toLowerCase() === wanted)

    for (const encoding of params.encodings as EncodingWithCodec[]) {
        if (match) {
            encoding.codec = {
                mimeType: match.mimeType,
                clockRate: match.clockRate,
                ...(match.channels ? { channels: match.channels } : {}),
                ...(match.sdpFmtpLine ? { sdpFmtpLine: match.sdpFmtpLine } : {})
            }
        } else {
            delete encoding.codec
        }
    }
}

/**
 * O que esta codificando de verdade, pra ficar embaixo da escolha.
 *
 * A medida da transmissao vence: e o que o navegador diz que abriu. Sem ela —
 * sem transmissao, sem ninguem recebendo (na malha, sozinho na sala nao ha
 * encoder rodando) ou navegador que nao conta — so vale avisar de antemao que
 * a GPU nao tem como assumir. No automatico, diz o que ele escolheu e por
 * que. `null` = nada a dizer.
 */
export function encoderStatus(
    chosen: ShareEncoder,
    inUse: ShareEncoder | null,
    gpuAvailable: boolean | null = null,
    auto = false
): string | null {
    if (inUse) {
        if (chosen === 'gpu' && inUse === 'cpu') {
            return 'A GPU nao assumiu: o navegador caiu pro processador'
        }
        return `Em uso agora: ${ENCODER_PRESETS[inUse].label}`
    }
    if (auto) {
        if (chosen === 'gpu') return 'O automatico escolheu a GPU: a placa codifica H.264'
        return gpuAvailable === false
            ? 'O automatico ficou na CPU: sem encoder de placa neste navegador'
            : 'O automatico ficou na CPU: este navegador nao diz se a placa codifica'
    }
    if (chosen === 'gpu' && gpuAvailable === false) {
        return 'Sem encoder de placa neste navegador: o H.264 vai rodar no processador'
    }
    return null
}
